import { useState, useEffect, useRef, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import scrollGridPlugin from '@fullcalendar/scrollgrid';
import { format } from 'date-fns';
import { bookingApi } from '../../api/bookingApi';
import type { BookingResponse, BookingCreateRequest } from '../../api/bookingApi';
import { employeeApi } from '../../api/employeeApi';
import type { EmployeeResponse } from '../../api/employeeApi';
import { serviceApi } from '../../api/serviceApi';
import type { ServiceResponse } from '../../api/serviceApi';
import { clientApi } from '../../api/clientApi';
import type { ClientSummary } from '../../api/clientApi';
import { specialtyApi } from '../../api/specialtyApi';
import type { SpecialtyResponse } from '../../api/specialtyApi';
import { employeeAvailabilityApi, type EmployeeAvailabilityOverride } from '../../api/employeeAvailabilityApi';
import { useSalonId } from '../../hooks/useSalonId';
import { useSubscription } from '../../hooks/useSubscription';
import { useToast } from '../../contexts/ToastContext';

// Import custom Cresta theme
import SearchableSelect from '../../components/ui/SearchableSelect';
import AgendaFilters from '../../components/ui/AgendaFilters';
import PrerequisiteAlertModal from '../../components/ui/PrerequisiteAlertModal';
import { getUserFriendlyError } from '../../utils/ErrorCatalog';
import '../../styles/calendar.css';

const AgendaView = () => {
    const salonId = useSalonId();
    const { employeeId, isOwner, isManager } = useSubscription();
    const toast = useToast();

    const [appointments, setAppointments] = useState<BookingResponse[]>([]);
    const [availabilityOverrides, setAvailabilityOverrides] = useState<EmployeeAvailabilityOverride[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<BookingResponse | null>(null);
    const [showEventModal, setShowEventModal] = useState(false);
    const [isEditingEvent, setIsEditingEvent] = useState(false);
    const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [viewRange, setViewRange] = useState<{ start: Date; end: Date } | null>(null);

    // View Mode State (Standard vs Resource)
    const [calendarView, setCalendarView] = useState<'timeGridWeek' | 'resourceTimeGridDay'>(
        (localStorage.getItem('agenda_calendar_view') as any) || 'timeGridWeek'
    );

    // Enforce team filter for non-admins
    useEffect(() => {
        if (!isOwner && !isManager && employeeId) {
            setSelectedTeamMembers([employeeId]);
        }
    }, [isOwner, isManager, employeeId]);

    // Dynamic data for dropdowns
    const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
    const [services, setServices] = useState<ServiceResponse[]>([]);
    const [specialties, setSpecialties] = useState<SpecialtyResponse[]>([]);
    const [isLoadingData, setIsLoadingData] = useState(false);

    // Specialty filtering
    const [selectedSpecialtyFilter, setSelectedSpecialtyFilter] = useState<string>('');
    const [filteredEmployees, setFilteredEmployees] = useState<EmployeeResponse[]>([]);
    const [filteredServices, setFilteredServices] = useState<ServiceResponse[]>([]);

    // Form submission state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);


    // Client autocomplete state (local cache strategy)
    const [clientSearchQuery, setClientSearchQuery] = useState('');
    const [clientSearchResults, setClientSearchResults] = useState<ClientSummary[]>([]);
    const [showClientDropdown, setShowClientDropdown] = useState(false);
    const [selectedClient, setSelectedClient] = useState<ClientSummary | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Search/Filter state for the calendar
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState<'ALL' | 'CLIENT' | 'EMPLOYEE'>('ALL');
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Advanced Filters State (Persisted)
    const [selectedTeamMembers, setSelectedTeamMembers] = useState<string[]>(() => {
        const saved = localStorage.getItem('agenda_team_filters');
        return saved ? JSON.parse(saved) : [];
    });
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>(() => {
        const saved = localStorage.getItem('agenda_status_filters');
        return saved ? JSON.parse(saved) : [];
    });
    const [selectedServicesFilter, setSelectedServicesFilter] = useState<string[]>(() => {
        const saved = localStorage.getItem('agenda_service_filters');
        return saved ? JSON.parse(saved) : [];
    });

    // Persist filters effect
    useEffect(() => {
        localStorage.setItem('agenda_team_filters', JSON.stringify(selectedTeamMembers));
        localStorage.setItem('agenda_status_filters', JSON.stringify(selectedStatuses));
        localStorage.setItem('agenda_service_filters', JSON.stringify(selectedServicesFilter));
        localStorage.setItem('agenda_calendar_view', calendarView);
    }, [selectedTeamMembers, selectedStatuses, selectedServicesFilter, calendarView]);

    // Prerequisite Alert Modal State
    const [showPrerequisiteAlert, setShowPrerequisiteAlert] = useState(false);
    const [prerequisiteAlertConfig, setPrerequisiteAlertConfig] = useState({
        title: '',
        message: '',
        redirectPath: '',
        redirectLabel: ''
    });

    // Map employees to AgendaFilters TeamMember format
    const teamMembers = useMemo(() => {
        return employees.map(emp => ({
            id: emp.id,
            name: `${emp.firstName} ${emp.lastName}`,
            photoUrl: emp.photoUrl,
            role: emp.role,
            isAvailable: true // TODO: Integrate with real-time availability in the future
        }));
    }, [employees]);

    // Form State
    const [formData, setFormData] = useState({
        clientId: '',
        clientName: '',
        clientPhone: '',
        clientEmail: '',
        employeeId: '',
        serviceId: '',
        time: '',
    });

    // Check prerequisites before creating appointments
    const checkPrerequisites = (): boolean => {
        if (employees.length === 0) {
            setPrerequisiteAlertConfig({
                title: 'Empleados Requeridos',
                message: 'Para agendar citas, necesitas tener al menos un empleado configurado en tu salón.',
                redirectPath: '/app/employees',
                redirectLabel: 'Ir a Empleados'
            });
            setShowPrerequisiteAlert(true);
            return false;
        }

        if (services.length === 0) {
            setPrerequisiteAlertConfig({
                title: 'Servicios Requeridos',
                message: 'Para agendar citas, necesitas tener al menos un servicio en tu catálogo.',
                redirectPath: '/app/services',
                redirectLabel: 'Ir a Servicios'
            });
            setShowPrerequisiteAlert(true);
            return false;
        }

        return true;
    };

    const handleNewAppointmentClick = () => {
        if (checkPrerequisites()) {
            setShowNewAppointmentModal(true);
        }
    };

    // Load employees, services, and clients when modal opens
    useEffect(() => {
        if (showNewAppointmentModal) {
            loadModalData();
        } else {
            // Reset form when modal closes
            resetForm();
        }
    }, [showNewAppointmentModal]);

    // Load employees AND services on mount for search filter and validation
    useEffect(() => {
        if (!salonId) return; // Don't load if no salon
        const loadInitialData = async () => {
            try {
                const [employeesData, servicesData] = await Promise.all([
                    employeeApi.getSalonEmployees(salonId),
                    serviceApi.getSalonServices(salonId)
                ]);

                // Defensive check: ensure data is an array
                if (Array.isArray(employeesData)) {
                    setEmployees(employeesData.filter(emp => emp.isActive));
                }

                if (Array.isArray(servicesData)) {
                    setServices(servicesData.filter(svc => svc.isActive));
                }

            } catch (error) {
                console.error('Error loading initial data:', error);
            }
        };
        loadInitialData();
    }, [salonId]);

    // Set initial selected team members when employees load
    useEffect(() => {
        if (employees.length > 0 && selectedTeamMembers.length === 0) {
            setSelectedTeamMembers(employees.map(e => e.id));
        }
    }, [employees]);

    // Local client search (instant, no API call)
    useEffect(() => {
        if (clientSearchQuery.trim().length < 2) {
            setClientSearchResults([]);
            setShowClientDropdown(false);
            return;
        }

        // Filter locally - 0ms latency!
        const results = clientApi.searchClientsLocally(clientSearchQuery);
        setClientSearchResults(results);
        setShowClientDropdown(true); // Always show dropdown when typing (even if no results)
    }, [clientSearchQuery]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowClientDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Intelligent Filtering: Filter employees by service requirements or manual specialty filter
    const [isFilteredByServiceConstraint, setIsFilteredByServiceConstraint] = useState(false);

    useEffect(() => {
        const selectedService = services.find(s => s.id === formData.serviceId);
        const requiredSpecialtyId = selectedService?.requiredSpecialtyId;

        // 1. Service filter: Calculate which employees are actually capable of doing this service
        let capableEmployees = [...employees];
        if (requiredSpecialtyId) {
            capableEmployees = capableEmployees.filter(emp =>
                emp.specialties.some(s => s.id === requiredSpecialtyId)
            );
            setIsFilteredByServiceConstraint(true);
        } else {
            setIsFilteredByServiceConstraint(false);
        }

        // 2. Manual filter: Apply the UI filter selected by the user
        let filteredEmps = [...capableEmployees];
        let filteredSvcs = [...services];

        if (selectedSpecialtyFilter) {
            // Filter Employees
            filteredEmps = filteredEmps.filter(emp =>
                emp.specialties.some(s => s.id === selectedSpecialtyFilter)
            );
            // Filter Services
            filteredSvcs = filteredSvcs.filter(svc =>
                svc.requiredSpecialtyId === selectedSpecialtyFilter || !svc.requiredSpecialtyId
            );
        }

        setFilteredEmployees(filteredEmps);
        setFilteredServices(filteredSvcs);

        // Auto-select first available or clear if current employee is filtered out
        if (filteredEmps.length > 0) {
            if (!filteredEmps.some(emp => emp.id === formData.employeeId)) {
                // Don't auto-select, let the user choose. Just clear invalid selection.
                if (formData.employeeId) {
                    setFormData(prev => ({ ...prev, employeeId: '' }));
                }
            }
        } else {
            setFormData(prev => ({ ...prev, employeeId: '' }));
        }
    }, [formData.serviceId, selectedSpecialtyFilter, employees, services]);

    const resetForm = () => {
        setFormData({
            clientId: '',
            clientName: '',
            clientPhone: '',
            clientEmail: '',
            employeeId: employees.length > 0 ? employees[0].id : '',
            serviceId: services.length > 0 ? services[0].id : '',
            time: ''
        });
        setSelectedClient(null);
        setClientSearchQuery('');
        setClientSearchResults([]);
        setShowClientDropdown(false);
        setSubmitError(null);
    };

    const handleClientSelect = (client: ClientSummary) => {
        setSelectedClient(client);
        setClientSearchQuery(client.fullName);
        setFormData(prev => ({
            ...prev,
            clientId: client.id,
            clientName: client.fullName,
            clientPhone: client.phone,
            clientEmail: '' // Summary doesn't include email
        }));
        setShowClientDropdown(false);
    };

    const handleClientInputChange = (value: string) => {
        setClientSearchQuery(value);
        setFormData(prev => ({
            ...prev,
            clientName: value,
            clientId: '' // Clear client ID when manually typing
        }));

        // If user clears the input, reset client selection
        if (!value.trim()) {
            setSelectedClient(null);
            setFormData(prev => ({
                ...prev,
                clientId: '',
                clientPhone: '',
                clientEmail: ''
            }));
        }
    };

    // Also reload active employees/services periodically or when modal opens if needed, 
    // but for now, load on mount is fine.

    const loadModalData = async () => {
        setIsLoadingData(true);
        setSubmitError(null);

        try {
            if (!salonId) {
                setSubmitError('Sin salón configurado. Por favor, completa primero la configuración.');
                return;
            }
            const [employeesData, servicesData, specialtiesData] = await Promise.all([
                employeeApi.getSalonEmployees(salonId),
                serviceApi.getSalonServices(salonId),
                specialtyApi.getSalonSpecialties(salonId),
                clientApi.getSalonClients(salonId) // Uses cache if available
            ]);

            // Defensive checks: ensure data is arrays before filtering
            const safeEmployeesData = Array.isArray(employeesData) ? employeesData : [];
            const safeServicesData = Array.isArray(servicesData) ? servicesData : [];
            const safeSpecialtiesData = Array.isArray(specialtiesData) ? specialtiesData : [];

            // Filter active employees and services
            const activeEmployees = safeEmployeesData.filter(emp => emp.isActive);
            const activeServices = safeServicesData.filter(svc => svc.isActive);

            setEmployees(activeEmployees);
            setServices(activeServices);
            setSpecialties(safeSpecialtiesData);
            setFilteredEmployees(activeEmployees); // Initialize with all employees
            setFilteredServices(activeServices); // Initialize with all services

            // Pre-select first employee and service if available
            if (activeEmployees.length > 0 && !formData.employeeId) {
                setFormData(prev => ({ ...prev, employeeId: activeEmployees[0].id }));
            }

        } catch (error) {
            console.error('Error loading modal data:', error);
            setSubmitError('Error al cargar los datos. Por favor, intenta de nuevo.');
            // Reset to empty arrays on error
            setEmployees([]);
            setServices([]);
            setSpecialties([]);
        } finally {
            setIsLoadingData(false);
        }
    };

    const fetchAppointments = async (start: Date, end: Date) => {
        if (!salonId) return; // Guard for null salonId
        setIsLoading(true);
        try {
            const startDateStr = format(start, 'yyyy-MM-dd');
            const endDateStr = format(end, 'yyyy-MM-dd');
            console.log('Fetching for range:', startDateStr, endDateStr);

            const [appointmentsData, availabilityData] = await Promise.all([
                bookingApi.getSalonAppointments(salonId, startDateStr, endDateStr),
                employeeAvailabilityApi.getSalonAvailability(salonId, startDateStr, endDateStr)
            ]);

            // Defensive check: ensure data is an array
            if (Array.isArray(appointmentsData)) {
                setAppointments(appointmentsData);
            } else {
                console.warn('Appointments data is not an array:', appointmentsData);
                setAppointments([]);
            }

            if (Array.isArray(availabilityData)) {
                setAvailabilityOverrides(availabilityData);
            } else {
                setAvailabilityOverrides([]);
            }
        } catch (error) {
            console.error("Failed to fetch data", error);
            setAppointments([]);
            setAvailabilityOverrides([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Re-fetch appointments when salonId becomes available (fixes initial load issue)
    useEffect(() => {
        if (salonId && viewRange) {
            fetchAppointments(viewRange.start, viewRange.end);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salonId]);

    // Filter appointments based on search query and advanced filters (memoized)
    const filteredAppointments = useMemo(() => {
        return appointments.filter(apt => {
            // 1. Team members filter
            if (selectedTeamMembers.length > 0 && !selectedTeamMembers.includes(apt.employeeId)) {
                return false;
            }

            // 2. Statuses filter
            if (selectedStatuses.length > 0 && !selectedStatuses.includes(apt.status)) {
                return false;
            }

            // 3. Services filter
            if (selectedServicesFilter.length > 0 && !selectedServicesFilter.includes(apt.serviceId)) {
                return false;
            }

            // 4. Search query (Text filtering)
            if (!searchQuery.trim()) return true;

            const query = searchQuery.toLowerCase();
            let matches = false;

            // Search by Client (if ALL or CLIENT selected)
            if (filterType === 'ALL' || filterType === 'CLIENT') {
                if (apt.clientName?.toLowerCase().includes(query)) matches = true;
                if (apt.clientPhone?.toLowerCase().includes(query)) matches = true;
            }

            // Search by Employee (if ALL or EMPLOYEE selected)
            if (!matches && (filterType === 'ALL' || filterType === 'EMPLOYEE')) {
                const employee = employees.find(e => e.id === apt.employeeId);
                if (employee) {
                    const employeeName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
                    if (employeeName.includes(query)) matches = true;
                }
            }

            return matches;
        });
    }, [appointments, searchQuery, employees, filterType, selectedTeamMembers, selectedStatuses, selectedServicesFilter]);

    // Transform filtered appointments to FullCalendar events (memoized)
    // Transform filtered appointments to FullCalendar events (memoized)
    const events = useMemo(() => {
        // 1. Map Appointments
        const appointmentEvents = filteredAppointments.map(apt => ({
            id: apt.id,
            title: `${apt.serviceName} - ${apt.clientName}`,
            start: apt.startTime,
            end: apt.endTime,
            extendedProps: {
                ...apt,
                itemType: 'appointment',
                employeePhotoUrl: employees.find(e => e.id === apt.employeeId)?.photoUrl
            },
            className: `event-${apt.status.toLowerCase().replace('_', '-')}`,
            resourceId: apt.employeeId // Useful if we implement resource view later
        }));

        // 2. Map Availability Overrides (Bloqueos)
        const filteredAvailability = availabilityOverrides.filter(avail => {
            // 1. Team members filter
            if (selectedTeamMembers.length > 0 && !selectedTeamMembers.includes(avail.employeeId)) {
                return false;
            }

            // 2. Search query filter
            if (searchQuery.trim()) {
                const employee = employees.find(e => e.id === avail.employeeId);
                if (employee) {
                    const employeeName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
                    return employeeName.includes(searchQuery.toLowerCase());
                }
                return false;
            }

            return true;
        });

        const availabilityEvents = filteredAvailability.map(avail => {
            const isAllDay = !avail.startTime;



            const labels: Record<string, string> = {
                'VACATION': 'Vacaciones',
                'SICK_LEAVE': 'Incapacidad',
                'PERSONAL_LEAVE': 'Permiso',
                'CUSTOM_HOURS': 'Horario Esp.',
                'BLOCKED': 'Bloqueado'
            };

            let startStr = avail.startDate;
            let endStr = avail.endDate;

            if (!isAllDay && avail.startTime && avail.endTime) {
                startStr = `${avail.startDate.split('T')[0]}T${avail.startTime}`;
                endStr = `${avail.endDate.split('T')[0]}T${avail.endTime}`;
            }

            const employee = employees.find(e => e.id === avail.employeeId);
            const employeeName = employee ? `${employee.firstName}` : '';

            return {
                id: `avail-${avail.id}`,
                title: `⛔ ${labels[avail.type] || 'Ausente'} (${employeeName})`,
                start: startStr,
                end: endStr,
                allDay: isAllDay,
                className: `event-avail-${avail.type.toLowerCase().replace('_', '-')}`, // Use CSS class for premium styling
                editable: false,
                extendedProps: { ...avail, itemType: 'availability' }
            };
        });

        return [...appointmentEvents, ...availabilityEvents];
    }, [filteredAppointments, availabilityOverrides, employees, searchQuery, filterType, selectedTeamMembers]);

    const handleDatesSet = (dateInfo: any) => {
        // FullCalendar 'end' is exclusive. We subtract 1 day to correct the range for our backend
        const end = new Date(dateInfo.end);
        end.setDate(end.getDate() - 1);

        setViewRange({ start: dateInfo.start, end });
        fetchAppointments(dateInfo.start, end);
    };

    const handleDateClick = (arg: any) => {
        if (!checkPrerequisites()) return;

        setSelectedDate(arg.date);
        setFormData(prev => ({
            ...prev,
            time: format(arg.date, "HH:mm")
        }));
        setShowNewAppointmentModal(true);
    };

    const handleEventClick = (clickInfo: any) => {
        // Check if it is an availability event
        const props = clickInfo.event.extendedProps;
        if (props.itemType === 'availability') {
            return;
        }

        const appointment = appointments.find(apt => apt.id === clickInfo.event.id);
        if (appointment) {
            setSelectedEvent(appointment);
            setShowEventModal(true);
        }
    };

    const handleCreateSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedDate) return;

        if (!formData.serviceId || !formData.employeeId) {
            setSubmitError("Por favor selecciona un servicio y un profesional.");
            return;
        }

        setIsSubmitting(true);
        setSubmitError(null);

        const [hours, minutes] = formData.time.split(':');
        const startDate = new Date(selectedDate);
        startDate.setHours(parseInt(hours), parseInt(minutes));

        // Validation: Cannot book in past if today
        const now = new Date();
        if (startDate.toDateString() === now.toDateString() && startDate < now) {
            setSubmitError("No es posible agendar citas en una hora pasada.");
            setIsSubmitting(false);
            return;
        }

        // Build request with either clientId OR clientName/clientPhone
        const request: BookingCreateRequest = {
            salonId: salonId!,
            employeeId: formData.employeeId,
            serviceId: formData.serviceId,
            startTime: startDate.toISOString(),
        };

        // Use clientId if a client was selected, otherwise use name/phone
        if (formData.clientId) {
            request.clientId = formData.clientId;
        } else {
            request.clientName = formData.clientName;
            request.clientPhone = formData.clientPhone;
        }

        try {
            await bookingApi.createAppointment(request);

            // Success! Close modal and refresh
            setShowNewAppointmentModal(false);
            toast.success('¡Cita creada exitosamente!');

            // Refresh calendar using current view range
            if (viewRange) {
                fetchAppointments(viewRange.start, viewRange.end);
            }

            // Reset form
            resetForm();
        } catch (error: any) {
            console.error('Error creating appointment:', error);

            // Use centralized error catalog
            const friendlyError = getUserFriendlyError(error);
            setSubmitError(friendlyError.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderEventContent = (eventInfo: any) => {
        const props = eventInfo.event.extendedProps;
        const isAvailability = props.itemType === 'availability';

        if (isAvailability) {
            return (
                <div className="fc-event-main-frame flex items-center gap-1 overflow-hidden p-1 h-full">
                    <div className="fc-event-title-container flex-grow min-w-0">
                        <div className="fc-event-title font-medium truncate text-xs">{eventInfo.event.title}</div>
                    </div>
                </div>
            );
        }

        // Appointment Event
        return (
            <div className="fc-event-main-frame flex items-center gap-1.5 overflow-hidden px-1.5 py-0.5 h-full w-full">
                {/* Avatar */}
                <div className="flex-shrink-0">
                    {props.employeePhotoUrl ? (
                        <img
                            src={props.employeePhotoUrl}
                            alt="E"
                            className="w-5 h-5 rounded-full object-cover border border-white/40 shadow-sm"
                        />
                    ) : (
                        <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[9px] font-bold text-white border border-white/40 shadow-sm">
                            {employees.find(e => e.id === props.employeeId)?.firstName.charAt(0) || 'E'}
                        </div>
                    )}
                </div>

                <div className="fc-event-title-container flex flex-col justify-center min-w-0 flex-grow">
                    <div className="fc-event-title font-bold text-xs truncate leading-tight">
                        {props.clientName}
                    </div>
                    <div className="text-[10px] opacity-90 truncate leading-tight">
                        {props.serviceName}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Agenda</h1>
                    <p className="text-gray-500">Gestiona las citas y horarios</p>
                </div>
                <button
                    onClick={handleNewAppointmentClick}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-light transition-colors font-medium cursor-pointer">
                    Nueva Cita
                </button>
            </div>

            {isLoading && (
                <div className="fixed top-4 right-4 bg-white p-2 rounded shadow text-sm text-primary z-50 animate-pulse">
                    Sincronizando...
                </div>
            )}

            {/* Search Filter */}
            <div className="mb-4 flex flex-col md:flex-row gap-4 max-w-2xl relative">
                <div className="relative flex-1">
                    <input
                        type="text"
                        placeholder={filterType === 'ALL' ? "Buscar..." : `Buscar por ${filterType === 'CLIENT' ? 'Cliente' : 'Empleado'}...`}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        value={searchQuery}
                        onChange={e => {
                            setSearchQuery(e.target.value);
                            setShowSuggestions(true);
                        }}
                        onFocus={() => setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    />
                    <svg
                        className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 cursor-pointer"
                        >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}

                    {/* Autocomplete Suggestions */}
                    {showSuggestions && searchQuery.trim().length > 0 && filterType !== 'ALL' && (
                        <div className="absolute top-full left-0 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-60 overflow-y-auto">
                            {(() => {
                                const suggestions = filterType === 'EMPLOYEE'
                                    ? employees.filter(e =>
                                        `${e.firstName} ${e.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
                                    ).map(e => ({ id: e.id, label: `${e.firstName} ${e.lastName}`, sub: e.role }))
                                    : clientApi.searchClientsLocally(searchQuery).map(c => ({ id: c.id, label: c.fullName, sub: c.phone }));

                                if (suggestions.length === 0) return null;

                                return suggestions.map(item => (
                                    <button
                                        key={item.id}
                                        className="w-full text-left px-4 py-2 hover:bg-gray-50 flex flex-col border-b border-gray-50 last:border-0 cursor-pointer"
                                        onClick={() => {
                                            setSearchQuery(item.label);
                                            setShowSuggestions(false);
                                        }}
                                    >
                                        <span className="text-sm font-medium text-gray-900">{item.label}</span>
                                        {item.sub && <span className="text-xs text-gray-500">{item.sub}</span>}
                                    </button>
                                ));
                            })()}
                        </div>
                    )}
                </div>

                <SearchableSelect
                    value={filterType}
                    onChange={(val) => setFilterType(val as 'ALL' | 'CLIENT' | 'EMPLOYEE')}
                    options={[
                        { value: "ALL", label: "Todo" },
                        { value: "CLIENT", label: "Clientes" },
                        { value: "EMPLOYEE", label: "Empleados" },
                    ]}
                    className="w-48"
                />

                <div className="flex-shrink-0">
                    <AgendaFilters
                        teamMembers={teamMembers}
                        selectedTeamMembers={selectedTeamMembers}
                        onTeamMembersChange={setSelectedTeamMembers}
                        selectedStatuses={selectedStatuses}
                        onStatusesChange={setSelectedStatuses}
                        services={services.map(s => ({ id: s.id, name: s.name }))}
                        selectedServices={selectedServicesFilter}
                        onServicesChange={setSelectedServicesFilter}
                        onClearAll={() => {
                            if (!isOwner && !isManager && employeeId) {
                                setSelectedTeamMembers([employeeId]);
                            } else {
                                setSelectedTeamMembers(employees.map(e => e.id));
                            }
                            setSelectedStatuses([]);
                            setSelectedServicesFilter([]);
                            setSearchQuery('');
                        }}
                        hideTeamSelector={!isOwner && !isManager}
                    />
                </div>
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div className="flex bg-gray-100 p-1 rounded-lg">
                    <button
                        onClick={() => setCalendarView('timeGridWeek')}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${calendarView === 'timeGridWeek'
                            ? 'bg-white text-primary shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Calendario
                    </button>
                    <button
                        onClick={() => setCalendarView('resourceTimeGridDay')}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${calendarView === 'resourceTimeGridDay'
                            ? 'bg-white text-primary shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Equipo (Columnas)
                    </button>
                </div>

                {searchQuery && (
                    <p className="text-sm text-gray-600">
                        Mostrando {filteredAppointments.length} de {appointments.length} cita{appointments.length !== 1 ? 's' : ''}
                    </p>
                )}
            </div>

            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                <FullCalendar
                    key={calendarView} // Force re-mount when switching view modes
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, resourceTimeGridPlugin, scrollGridPlugin]}
                    initialView={calendarView}
                    headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth'
                    }}
                    buttonText={{
                        today: 'Hoy',
                        month: 'Mes'
                    }}
                    locale="es"
                    datesSet={handleDatesSet}
                    slotDuration="00:30:00"
                    slotMinTime="08:00:00"
                    slotMaxTime="20:00:00"
                    // Resource View Configuration
                    resources={
                        calendarView === 'resourceTimeGridDay'
                            ? employees
                                .filter(e => selectedTeamMembers.length === 0 || selectedTeamMembers.includes(e.id))
                                .map(e => ({
                                    id: e.id,
                                    title: e.firstName,
                                    // Optional: specific color per employee logic here
                                }))
                            : undefined
                    }
                    resourceAreaWidth="15%" // Width of resource column if needed (usually for timeline view, but safe to add)

                    businessHours={{
                        daysOfWeek: [1, 2, 3, 4, 5, 6],
                        startTime: '08:00',
                        endTime: '20:00'
                    }}
                    events={events}
                    dateClick={handleDateClick}
                    eventClick={handleEventClick}
                    eventContent={renderEventContent}
                    height="auto"
                    nowIndicator={true}
                    allDaySlot={false}
                    slotLabelFormat={{
                        hour: 'numeric',
                        minute: '2-digit',
                        meridiem: 'short'
                    }}
                />
            </div>

            {/* Event Details Modal */}
            {showEventModal && selectedEvent && (
                <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowEventModal(false)}>
                    <div className="modal-content max-w-md p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-bold text-gray-900">
                                {isEditingEvent ? 'Editar Cita' : 'Detalles de la Cita'}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowEventModal(false);
                                    setIsEditingEvent(false);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ✕
                            </button>
                        </div>

                        {/* View Mode */}
                        {!isEditingEvent ? (
                            <div className="space-y-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Servicio</label>
                                    <p className="text-gray-900 font-medium">{selectedEvent.serviceName}</p>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Cliente</label>
                                    <p className="text-gray-900 font-medium">{selectedEvent.clientName}</p>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Estilista</label>
                                    <p className="text-gray-900">
                                        {employees.find(e => e.id === selectedEvent.employeeId)?.firstName || 'Estilista'}
                                        {' '}
                                        {employees.find(e => e.id === selectedEvent.employeeId)?.lastName || ''}
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Hora Inicio</label>
                                        <p className="text-gray-900">{format(new Date(selectedEvent.startTime), 'h:mm a')}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Hora Fin</label>
                                        <p className="text-gray-900">{format(new Date(selectedEvent.endTime), 'h:mm a')}</p>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Estado</label>
                                    <p className={`inline-block px-2 py-1 rounded-full text-xs font-medium ml-2 ${selectedEvent.status === 'CONFIRMED' ? 'bg-emerald-100 text-emerald-700' :
                                        selectedEvent.status === 'SCHEDULED' ? 'bg-teal-100 text-teal-700' :
                                            selectedEvent.status === 'IN_PROGRESS' ? 'bg-orange-100 text-orange-700' :
                                                selectedEvent.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                                                    'bg-gray-100 text-gray-700'
                                        }`}>
                                        {{
                                            'SCHEDULED': 'Agendada',
                                            'CONFIRMED': 'Confirmada',
                                            'IN_PROGRESS': 'En Progreso',
                                            'COMPLETED': 'Completada',
                                            'CANCELLED': 'Cancelada',
                                            'NO_SHOW': 'No Asistió'
                                        }[selectedEvent.status] || selectedEvent.status}
                                    </p>
                                </div>

                                <div className="pt-4 flex gap-3">
                                    <button
                                        onClick={() => {
                                            setFormData(prev => ({
                                                ...prev,
                                                employeeId: selectedEvent.employeeId,
                                            }));
                                            if (employees.length === 0) loadModalData();
                                            setIsEditingEvent(true);
                                        }}
                                        className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium">
                                        Editar
                                    </button>
                                    <button
                                        onClick={() => setShowEventModal(false)}
                                        className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium">
                                        Cerrar
                                    </button>
                                </div>
                            </div>
                        ) : (
                            // Edit Mode
                            <div className="space-y-4">
                                {isLoadingData ? (
                                    <div className="py-8 text-center">
                                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                        <p className="mt-2 text-sm text-gray-500">Cargando datos...</p>
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <SearchableSelect
                                                label="Cambiar Estilista"
                                                placeholder="Seleccionar estilista..."
                                                options={employees
                                                    .filter(e => e.isActive)
                                                    .map(emp => ({
                                                        value: emp.id,
                                                        label: `${emp.firstName} ${emp.lastName}`,
                                                        subLabel: emp.specialties.map(s => s.name).join(', ')
                                                    }))}
                                                value={formData.employeeId}
                                                onChange={(val) => setFormData(prev => ({ ...prev, employeeId: val }))}
                                            />
                                        </div>

                                        <SearchableSelect
                                            label="Cambiar Estado"
                                            placeholder="Seleccionar estado..."
                                            options={[
                                                { value: 'SCHEDULED', label: 'Agendada' },
                                                { value: 'CONFIRMED', label: 'Confirmada' },
                                                { value: 'IN_PROGRESS', label: 'En Progreso' },
                                                { value: 'COMPLETED', label: 'Completada' },
                                                { value: 'CANCELLED', label: 'Cancelada' },
                                                { value: 'NO_SHOW', label: 'No Asistió' }
                                            ]}
                                            value={selectedEvent.status}
                                            onChange={async (val) => {
                                                setSelectedEvent(prev => prev ? ({ ...prev, status: val as any }) : null);
                                            }}
                                        />

                                        {submitError && (
                                            <div className="text-xs text-red-600 font-medium">
                                                {submitError}
                                            </div>
                                        )}

                                        <div className="pt-4 flex gap-3">
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        setIsSubmitting(true);
                                                        setSubmitError(null);

                                                        await bookingApi.updateAppointment(selectedEvent.id, {
                                                            employeeId: formData.employeeId,
                                                            status: selectedEvent.status
                                                        });

                                                        if (viewRange) {
                                                            await fetchAppointments(viewRange.start, viewRange.end);
                                                        }

                                                        setShowEventModal(false);
                                                        setIsEditingEvent(false);
                                                        toast.success('¡Cita actualizada exitosamente!');
                                                    } catch (err: any) {
                                                        console.error('Update error:', err);
                                                        const friendlyError = getUserFriendlyError(err);
                                                        setSubmitError(friendlyError.message);
                                                    } finally {
                                                        setIsSubmitting(false);
                                                    }
                                                }}
                                                disabled={isSubmitting}
                                                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium flex justify-center">
                                                {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
                                            </button>
                                            <button
                                                onClick={() => setIsEditingEvent(false)}
                                                disabled={isSubmitting}
                                                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium">
                                                Cancelar
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* New Appointment Modal */}
            {showNewAppointmentModal && (
                <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowNewAppointmentModal(false)}>
                    <div className="modal-content max-w-md p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-bold text-gray-900">Nueva Cita</h3>
                            <button
                                onClick={() => setShowNewAppointmentModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ✕
                            </button>
                        </div>

                        {isLoadingData ? (
                            <div className="py-8 text-center">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                <p className="mt-2 text-sm text-gray-500">Cargando datos...</p>
                            </div>
                        ) : (
                            <form onSubmit={handleCreateSubmit} className="space-y-4">
                                {/* Client Autocomplete */}
                                <div className="relative" ref={dropdownRef}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nombre del Cliente <span className="text-red-500">*</span>
                                        {selectedClient && (
                                            <span className="ml-2 text-xs text-emerald-600">
                                                ✓ Cliente existente
                                            </span>
                                        )}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            required
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                            value={clientSearchQuery}
                                            onChange={e => handleClientInputChange(e.target.value)}
                                            onFocus={() => {
                                                if (clientSearchResults.length > 0) {
                                                    setShowClientDropdown(true);
                                                }
                                            }}
                                            disabled={isSubmitting}
                                            placeholder="Escribe para buscar clientes..."
                                        />
                                    </div>

                                    {/* Autocomplete Dropdown */}
                                    {showClientDropdown && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                            {clientSearchResults.length > 0 ? (
                                                // Show search results
                                                clientSearchResults.map(client => (
                                                    <button
                                                        key={client.id}
                                                        type="button"
                                                        onClick={() => handleClientSelect(client)}
                                                        className="w-full text-left px-4 py-3 hover:bg-emerald-50 border-b border-gray-100 last:border-b-0 transition-colors"
                                                    >
                                                        <div className="font-medium text-gray-900">{client.fullName}</div>
                                                        <div className="text-sm text-gray-500">{client.phone}</div>
                                                    </button>
                                                ))
                                            ) : clientSearchQuery.trim().length >= 2 ? (
                                                // Show "Create New Client" when no results found
                                                <div className="px-4 py-3">
                                                    <div className="text-sm text-gray-500 mb-1">
                                                        No se encontraron clientes con "{clientSearchQuery}"
                                                    </div>
                                                    <div className="text-sm text-emerald-600 flex items-center">
                                                        <span className="mr-1 text-lg font-bold">+</span>
                                                        <span>Creando nuevo cliente: <strong>{clientSearchQuery}</strong></span>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono del Cliente</label>
                                    <input
                                        type="tel"
                                        required={!formData.clientId} // Only required if no client selected
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                        value={formData.clientPhone}
                                        onChange={e => setFormData({ ...formData, clientPhone: e.target.value })}
                                        disabled={isSubmitting || !!selectedClient} // Disable if client selected
                                        placeholder={selectedClient ? "Completado automáticamente" : "Ingresa el número de teléfono"}
                                    />
                                </div>

                                <div>
                                    <SearchableSelect
                                        label="Filtrar por Especialidad (Opcional)"
                                        placeholder="Seleccionar especialidad..."
                                        options={[
                                            { value: "", label: "Todos los Estilistas" },
                                            ...specialties.map(s => ({ value: s.id, label: s.name }))
                                        ]}
                                        value={selectedSpecialtyFilter}
                                        onChange={(val) => setSelectedSpecialtyFilter(val)}
                                        disabled={isSubmitting}
                                    />
                                </div>

                                <div>
                                    <SearchableSelect
                                        label="Estilista"
                                        placeholder="Seleccionar estilista..."
                                        required
                                        options={filteredEmployees.map(emp => ({
                                            value: emp.id,
                                            label: `${emp.firstName} ${emp.lastName}`,
                                            subLabel: emp.specialties.map(s => s.name).join(', ')
                                        }))}
                                        value={formData.employeeId}
                                        onChange={(val) => setFormData(prev => ({ ...prev, employeeId: val }))}
                                        disabled={isSubmitting}
                                    />

                                    {filteredEmployees.length === 0 && (
                                        <div className="mt-2 text-xs bg-amber-50 text-amber-800 p-2 rounded-md border border-amber-200 flex items-start">
                                            <span className="mr-1">⚠️</span>
                                            <div>
                                                {selectedSpecialtyFilter ? (
                                                    <>
                                                        <strong>Sin resultados:</strong> Ningún estilista con la especialidad <strong>"{specialties.find(s => s.id === selectedSpecialtyFilter)?.name}"</strong> {isFilteredByServiceConstraint ? 'está calificado para' : 'está disponible para'} este servicio.
                                                    </>
                                                ) : (
                                                    <>
                                                        <strong>Sin cobertura:</strong> Ningún estilista tiene la especialidad requerida.
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <SearchableSelect
                                        label="Servicio"
                                        placeholder="Seleccionar servicio..."
                                        required
                                        options={filteredServices.map(svc => ({
                                            value: svc.id,
                                            label: svc.name,
                                            subLabel: `${new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(svc.price)} (${svc.durationMinutes} min)`
                                        }))}
                                        value={formData.serviceId}
                                        onChange={(val) => setFormData(prev => ({ ...prev, serviceId: val }))}
                                        disabled={isSubmitting}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                                        <input
                                            type="date"
                                            required
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                            value={selectedDate ? format(selectedDate, 'yyyy-MM-dd') : ''}
                                            onChange={e => {
                                                if (e.target.value) {
                                                    setSelectedDate(new Date(e.target.value + 'T12:00:00'));
                                                }
                                            }}
                                            disabled={isSubmitting}
                                            min={format(new Date(), 'yyyy-MM-dd')}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
                                        <input
                                            type="time"
                                            required
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                            value={formData.time}
                                            onChange={e => setFormData({ ...formData, time: e.target.value })}
                                            disabled={isSubmitting}
                                            step="900"
                                        />
                                    </div>
                                </div>

                                {submitError && (
                                    <div className="bg-red-50 border border-red-200 p-3 rounded-lg text-sm text-red-700 flex items-start gap-2">
                                        <span className="text-red-500 font-bold">⚠</span>
                                        <span>{submitError}</span>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Creando...
                                        </>
                                    ) : (
                                        'Crear Cita'
                                    )}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}



            {/* Prerequisite Alert Modal */}
            <PrerequisiteAlertModal
                isOpen={showPrerequisiteAlert}
                onClose={() => setShowPrerequisiteAlert(false)}
                title={prerequisiteAlertConfig.title}
                message={prerequisiteAlertConfig.message}
                redirectPath={prerequisiteAlertConfig.redirectPath}
                redirectLabel={prerequisiteAlertConfig.redirectLabel}
            />
        </div>
    );
};

export default AgendaView;
